import { useEffect, useState, useCallback } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useEntity } from "@/context/EntityContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useCurrencyRail } from "@/context/CurrencyRailContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePlaidLink } from "react-plaid-link";
import { Banknote, Building2, Plus, RefreshCw, Trash2, ShieldCheck, AlertTriangle, BarChart3, Pencil, Wallet, CheckCircle2, AlertCircle, Clock, ArrowLeftRight } from "lucide-react";
import { StatCard } from "@/components/DashboardWidgets";
import TransferDialog from "@/components/TransferDialog";
import PayPalConnectCard from "@/components/PayPalConnectCard";
import { formatCurrency } from "@/lib/format";
import { toast } from "sonner";
import { formatDistanceToNow, format as fmtDate, parseISO } from "date-fns";
import { Link } from "react-router-dom";

// ============================================================
// Reconciliation status hook + badge (iter 41)
// Shared by BanksOverview + ManualBanks so every account row shows its
// current reconciliation health at a glance.
// ============================================================
function useReconStatus() {
  const [byId, setById] = useState({});
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/reconciliation/status");
        if (!alive) return;
        const m = {};
        (data || []).forEach((r) => { m[r.account_id] = r; });
        setById(m);
      } catch { /* silent — badge is optional context */ }
    })();
    return () => { alive = false; };
  }, []);
  return byId;
}

function ReconBadge({ status }) {
  if (!status) return null;
  const link = `/financials?tab=reconciliation&account=${status.account_id}`;
  // Never reconciled — nudge the user.
  if (!status.last_reconciled) {
    return (
      <Link to={link} data-testid={`recon-badge-${status.account_id}`}
            className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[var(--track)] text-[var(--muted-text)] hover:bg-[var(--brand-light)] hover:text-[var(--brand-text)] transition">
        <Clock className="w-3 h-3" /> Not reconciled
        {status.unreconciled_count > 0 ? ` · ${status.unreconciled_count} open` : ""}
      </Link>
    );
  }
  let dateLabel = status.statement_end || status.last_reconciled;
  try { dateLabel = fmtDate(parseISO(dateLabel), "MMM d, yyyy"); } catch { /* keep raw */ }
  if (status.is_balanced) {
    return (
      <Link to={link} data-testid={`recon-badge-${status.account_id}`}
            className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#E8F5EC] text-[#2F6B4A] hover:opacity-80 transition">
        <CheckCircle2 className="w-3 h-3" /> Reconciled {dateLabel} · balanced
      </Link>
    );
  }
  return (
    <Link to={link} data-testid={`recon-badge-${status.account_id}`}
          className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#FFEBEE] text-[#B4283A] hover:opacity-80 transition">
      <AlertCircle className="w-3 h-3" /> Reconciled {dateLabel} · off by {formatCurrency(status.difference, "USD")}
    </Link>
  );
}

function PlaidConnectButton({ onLinked }) {
  const [linkToken, setLinkToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchToken = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post("/plaid/link/token");
      setLinkToken(data.link_token);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Could not create link token";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchToken(); }, [fetchToken]);

  const config = {
    token: linkToken,
    onSuccess: async (public_token, metadata) => {
      try {
        await api.post("/plaid/link/exchange", { public_token, metadata });
        toast.success(`${metadata.institution?.name || "Bank"} connected`);
        onLinked?.();
        fetchToken();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not link account");
      }
    },
    onExit: (err) => {
      if (err) toast.error(err.display_message || err.error_message || "Link cancelled");
    },
  };

  const { open, ready } = usePlaidLink(config);

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        data-testid="plaid-connect-btn"
        disabled={!ready || loading || !!error}
        onClick={() => open()}
        className="rounded-md btn-brand h-11 px-5"
      >
        <Plus className="w-4 h-4 mr-2" /> {loading ? "Preparing..." : "Connect via Plaid"}
      </Button>
      {error && (
        <div data-testid="plaid-error" className="text-xs text-[#B4283A] max-w-xs text-right">
          {error}
        </div>
      )}
    </div>
  );
}

function MonoConnectButton({ onLinked }) {
  const [publicKey, setPublicKey] = useState(null);
  const [scriptLoaded, setScriptLoaded] = useState(!!window.Connect);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/mono/public-key")
      .then(({ data }) => setPublicKey(data.public_key))
      .catch((e) => setError(e.response?.data?.detail || "Mono isn't configured"));
    if (window.Connect) { setScriptLoaded(true); return; }
    const existing = document.querySelector('script[src="https://connect.withmono.com/connect.js"]');
    if (existing) {
      existing.addEventListener("load", () => setScriptLoaded(true));
      return;
    }
    const s = document.createElement("script");
    s.src = "https://connect.withmono.com/connect.js";
    s.async = true;
    s.onload = () => setScriptLoaded(true);
    s.onerror = () => setError("Could not load Mono widget script. Check your network.");
    document.body.appendChild(s);
  }, []);

  const openMono = () => {
    if (!window.Connect || !publicKey) {
      toast.error("Mono widget is still loading — try again in a moment.");
      return;
    }
    setLoading(true);
    try {
      const conn = new window.Connect({
        key: publicKey,
        onSuccess: async ({ code }) => {
          try {
            const { data } = await api.post("/mono/link/exchange", { code });
            toast.success(`${data.institution_name} connected`);
            onLinked?.();
          } catch (e) {
            toast.error(e.response?.data?.detail || "Could not link Mono account");
          } finally {
            setLoading(false);
          }
        },
        onClose: () => setLoading(false),
        onEvent: () => {},
      });
      conn.setup({ scope: "auth" });
      conn.open();
    } catch (e) {
      setLoading(false);
      toast.error(`Mono widget failed to open: ${e.message || e}`);
    }
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        data-testid="mono-connect-btn"
        disabled={!scriptLoaded || !publicKey || loading || !!error}
        onClick={openMono}
        className="rounded-md btn-brand h-11 px-5"
      >
        <Plus className="w-4 h-4 mr-2" /> {loading ? "Linking..." : "Connect via Mono"}
      </Button>
      {error && (
        <div data-testid="mono-error" className="text-xs text-[#B4283A] max-w-xs text-right">
          {error}
        </div>
      )}
    </div>
  );
}

export default function Banks() {
  const [plaidItems, setPlaidItems] = useState([]);
  const [monoItems, setMonoItems] = useState([]);
  const [syncingId, setSyncingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // {provider, id}
  const [status, setStatus] = useState(null); // {plaid: {env, is_production}, mono: {env, is_production}}
  const [transferOpen, setTransferOpen] = useState(false);
  const { currentEntity, isConsolidated } = useEntity();

  const load = useCallback(async () => {
    try {
      const [{ data: p }, { data: m }, { data: s }] = await Promise.all([
        api.get("/plaid/items"),
        api.get("/mono/items"),
        api.get("/banks/status"),
      ]);
      setPlaidItems(p);
      setMonoItems(m);
      setStatus(s);
    } catch (err) {
      console.warn("[Banks] load failed:", err?.message);
      toast.error("Couldn't load bank connections");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sync = async (provider, id) => {
    setSyncingId(id);
    try {
      const path = provider === "plaid" ? "/plaid/transactions/sync" : "/mono/transactions/sync";
      const { data } = await api.post(`${path}?item_id=${id}`);
      toast.success(`Synced · +${data.added ?? 0}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncingId(null);
    }
  };

  const syncAll = async (provider) => {
    setSyncingId(`all-${provider}`);
    try {
      const path = provider === "plaid" ? "/plaid/transactions/sync" : "/mono/transactions/sync";
      const { data } = await api.post(path);
      toast.success(`Synced all · +${data.added ?? 0}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncingId(null);
    }
  };

  const unlink = async () => {
    if (!deleteTarget) return;
    const path = deleteTarget.provider === "plaid" ? "/plaid/items" : "/mono/items";
    await api.delete(`${path}/${deleteTarget.id}`);
    toast.success("Bank unlinked");
    setDeleteTarget(null);
    load();
  };

  const renderItem = (item, provider) => (
    <div key={item.id} data-testid={`bank-row-${item.id}`} className="card-flat p-6 hover-lift">
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 rounded-md bg-[#2F4F4F] text-[#FDFCF8] flex items-center justify-center">
          <Banknote className="w-5 h-5" />
        </div>
        <div className="flex gap-1">
          <Button data-testid={`bank-sync-${item.id}`} size="icon" variant="ghost"
            onClick={() => sync(provider, item.id)} disabled={syncingId === item.id} aria-label="Sync">
            <RefreshCw className={`w-4 h-4 ${syncingId === item.id ? "animate-spin" : ""}`} />
          </Button>
          <Button data-testid={`bank-unlink-${item.id}`} size="icon" variant="ghost"
            onClick={() => setDeleteTarget({ provider, id: item.id })} aria-label="Unlink">
            <Trash2 className="w-4 h-4 text-[#E63946]" />
          </Button>
        </div>
      </div>
      <div className="mt-4">
        <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">
          {provider === "plaid" ? "Plaid" : "Mono"} · {item.currency || (provider === "plaid" ? "USD" : "NGN")}
        </div>
        <h3 className="font-display text-xl mt-1">{item.institution_name}</h3>
      </div>
      {provider === "plaid" && (
        <div className="mt-3 space-y-1">
          {item.accounts?.map((a) => (
            <div key={a.account_id} className="text-sm text-[var(--muted-text)]">
              · {a.name} {a.mask ? `···${a.mask}` : ""} <span className="text-xs">({a.subtype})</span>
            </div>
          ))}
        </div>
      )}
      {provider === "mono" && (
        <div className="mt-3 text-sm text-[var(--muted-text)]">
          · {item.account_name || "Account"} {item.account_number ? `···${item.account_number.slice(-4)}` : ""}
        </div>
      )}
      <div className="mt-4 text-xs text-[var(--muted-text)]">
        Last synced {item.last_sync ? formatDistanceToNow(new Date(item.last_sync), { addSuffix: true }) : "never"}
      </div>
    </div>
  );

  return (
    <AppShell>
      <div className="mb-8">
        <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Zero-manual-entry</div>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-4xl mt-2">Banks</h1>
            <p className="text-sm text-[var(--muted-text)] mt-1">
              Connect banks in the US via Plaid, and Nigerian banks via Mono.
            </p>
          </div>
          <Button
            data-testid="banks-new-transfer-btn"
            variant="outline"
            className="rounded-full h-11 px-4"
            onClick={() => setTransferOpen(true)}
          >
            <ArrowLeftRight className="w-4 h-4 mr-2" /> New Transfer
          </Button>
        </div>
      </div>

      {isConsolidated ? (
        <div data-testid="banks-consolidated-warning" className="card-flat p-4 mb-6 border-l-4 border-l-[#D4A017] flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-[#D4A017] mt-0.5 shrink-0" />
          <div className="text-sm">
            <b>You&apos;re viewing Consolidated.</b> Pick a specific entity from the top-bar switcher before connecting a bank — otherwise the tool won&apos;t know which set of books the transactions should land in.
          </div>
        </div>
      ) : currentEntity ? (
        <div data-testid="banks-entity-scope" className="card-flat p-4 mb-6 border-l-4 flex items-center gap-3"
             style={{ borderLeftColor: currentEntity.color }}>
          <div className="w-9 h-9 rounded-md flex items-center justify-center text-lg shrink-0"
               style={{ backgroundColor: `${currentEntity.color}22`, color: currentEntity.color }}>
            {currentEntity.icon}
          </div>
          <div className="text-sm">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Syncing into</div>
            <div className="font-medium">{currentEntity.name}</div>
            <div className="text-xs text-[var(--muted-text)]">
              Transactions from banks connected while this entity is active will land in this set of books. Switch entities from the sidebar to point new connections elsewhere.
            </div>
          </div>
        </div>
      ) : null}

      <Tabs defaultValue="overview">
        <TabsList data-testid="banks-tabs">
          <TabsTrigger value="overview" data-testid="tab-banks-overview"><BarChart3 className="w-4 h-4 mr-1.5" /> Overview</TabsTrigger>
          <TabsTrigger value="plaid" data-testid="tab-plaid">Plaid · US / EU</TabsTrigger>
          <TabsTrigger value="mono" data-testid="tab-mono">Mono · Nigeria</TabsTrigger>
          <TabsTrigger value="manual" data-testid="tab-manual"><Wallet className="w-4 h-4 mr-1.5" /> Manual</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-6">
          <BanksOverview />
        </TabsContent>

        <TabsContent value="manual" className="space-y-6 mt-6">
          <ManualBanks onChange={load} />
        </TabsContent>

        <TabsContent value="plaid" className="space-y-6 mt-6">
          {status?.plaid?.is_production ? (
            <div data-testid="plaid-mode-banner" className="card-flat p-4 flex items-start gap-3 border-l-4 border-l-[#4C956C]">
              <ShieldCheck className="w-5 h-5 text-[#4C956C] mt-0.5 shrink-0" />
              <div className="text-sm text-[var(--muted-text)]">
                Plaid · <b className="text-[#4C956C]">Production</b>. Real banks accept your actual login credentials — 256-bit encrypted, credentials never touch our servers.
              </div>
            </div>
          ) : (
            <div data-testid="plaid-mode-banner" className="card-flat p-4 flex items-start gap-3 border-l-4 border-l-[#D4A017]">
              <AlertTriangle className="w-5 h-5 text-[#D4A017] mt-0.5 shrink-0" />
              <div className="text-sm text-[var(--muted-text)]">
                Plaid · <b className="text-[#D4A017]">Sandbox mode</b> — real bank credentials will be rejected. Use test creds{" "}
                <span className="font-mono-data text-[var(--text)]">user_good / pass_good</span>.{" "}
                To connect real US banks, upgrade to Production keys —{" "}
                <a href="https://dashboard.plaid.com/team/keys" target="_blank" rel="noreferrer"
                   className="text-[var(--brand)] underline hover:text-[var(--brand-hover)]" data-testid="plaid-upgrade-link">
                  get keys from the Plaid dashboard
                </a>{" "}
                and set <span className="font-mono-data text-[var(--text)]">PLAID_ENV=production</span>.
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2 justify-end">
            {plaidItems.length > 0 && (
              <Button data-testid="sync-all-plaid" variant="outline"
                onClick={() => syncAll("plaid")} disabled={syncingId === "all-plaid"} className="rounded-full h-11 px-4">
                <RefreshCw className={`w-4 h-4 mr-2 ${syncingId === "all-plaid" ? "animate-spin" : ""}`} /> Sync all
              </Button>
            )}
            <PlaidConnectButton onLinked={load} />
          </div>
          {plaidItems.length === 0 ? (
            <div data-testid="plaid-empty" className="card-flat p-12 text-center">
              <Building2 className="w-8 h-8 mx-auto text-[var(--muted-text)]" />
              <h3 className="font-display text-xl mt-3">No Plaid banks connected</h3>
            </div>
          ) : (
            <div data-testid="plaid-list" className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {plaidItems.map((it) => renderItem(it, "plaid"))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="mono" className="space-y-6 mt-6">
          {status?.mono?.is_production ? (
            <div data-testid="mono-mode-banner" className="card-flat p-4 flex items-start gap-3 border-l-4 border-l-[#4C956C]">
              <ShieldCheck className="w-5 h-5 text-[#4C956C] mt-0.5 shrink-0" />
              <div className="text-sm text-[var(--muted-text)]">
                Mono · <b className="text-[#4C956C]">Live</b>. Real Nigerian bank credentials accepted. NGN transactions auto-import.
              </div>
            </div>
          ) : (
            <div data-testid="mono-mode-banner" className="card-flat p-4 flex items-start gap-3 border-l-4 border-l-[#D4A017]">
              <AlertTriangle className="w-5 h-5 text-[#D4A017] mt-0.5 shrink-0" />
              <div className="text-sm text-[var(--muted-text)]">
                Mono · <b className="text-[#D4A017]">Test mode</b> — pick any bank in the widget and use the sandbox credentials Mono shows. To connect real Nigerian banks,{" "}
                <a href="https://app.mono.co/apps" target="_blank" rel="noreferrer"
                   className="text-[var(--brand)] underline hover:text-[var(--brand-hover)]" data-testid="mono-upgrade-link">
                  get Live keys from the Mono dashboard
                </a>{" "}
                and update <span className="font-mono-data text-[var(--text)]">MONO_PUBLIC_KEY</span> / <span className="font-mono-data text-[var(--text)]">MONO_SECRET_KEY</span>.
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2 justify-end">
            {monoItems.length > 0 && (
              <Button data-testid="sync-all-mono" variant="outline"
                onClick={() => syncAll("mono")} disabled={syncingId === "all-mono"} className="rounded-full h-11 px-4">
                <RefreshCw className={`w-4 h-4 mr-2 ${syncingId === "all-mono" ? "animate-spin" : ""}`} /> Sync all
              </Button>
            )}
            <MonoConnectButton onLinked={load} />
          </div>
          {monoItems.length === 0 ? (
            <div data-testid="mono-empty" className="card-flat p-12 text-center">
              <Building2 className="w-8 h-8 mx-auto text-[var(--muted-text)]" />
              <h3 className="font-display text-xl mt-3">No Nigerian banks connected</h3>
            </div>
          ) : (
            <div data-testid="mono-list" className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {monoItems.map((it) => renderItem(it, "mono"))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* PayPal integration (iter 63) — sits below the bank tabs */}
      <div className="mt-6">
        <PayPalConnectCard />
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlink this bank?</AlertDialogTitle>
            <AlertDialogDescription>
              Existing imported transactions remain, but no new ones will be pulled from this institution.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="bank-unlink-confirm"
              onClick={unlink}
              className="bg-[#E63946] hover:bg-[#c92e3b] text-white"
            >
              Unlink
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <TransferDialog
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        onDone={() => { setTransferOpen(false); load(); }}
      />
    </AppShell>
  );
}
// ============================================================
// Banks Overview — aggregated bank balances across all providers
// USD (Plaid) and NGN (Mono / manual) are shown as separate rails.
// When the sidebar FX toggle is ON, non-primary balances are also
// shown converted into the user's primary currency for a single
// unified view.
// ============================================================
function BanksOverview() {
  const [data, setData] = useState(null);
  const reconStatus = useReconStatus();
  const currency = useCurrency() || {};
  const { convertOn, convert, format, primary, ensureRates } = currency;
  const { activeRail, isActive } = useCurrencyRail();

  const load = async () => {
    try {
      const { data } = await api.get("/banks/summary");
      setData(data);
    } catch (err) {
      console.warn("[Banks] summary failed:", err?.message);
      toast.error("Couldn't load bank summary");
    }
  };
  useEffect(() => { load(); }, []);

  // Preload FX rates for both USD & NGN so toggle flips are instant.
  useEffect(() => {
    if (convertOn && ensureRates) {
      ensureRates("USD").catch(() => {});
      ensureRates("NGN").catch(() => {});
    }
  }, [convertOn, ensureRates]);

  if (!data) return <div className="text-sm text-[var(--muted-text)]">Loading…</div>;

  const totalConnected = data.counts.plaid + data.counts.mono;
  const manualByCcy = data.totals.manual_by_currency || {};
  const plaidByCcy = data.totals.plaid_by_currency || {};
  const monoByCcy = data.totals.mono_by_currency || {};

  const usdManual = manualByCcy.USD || 0;
  const ngnManual = manualByCcy.NGN || 0;
  const usdConnected = (plaidByCcy.USD || 0) + (monoByCcy.USD || 0);
  const ngnConnected = (plaidByCcy.NGN || 0) + (monoByCcy.NGN || 0);

  // Format helper: native + optional converted equivalent when FX toggle is on.
  const renderBalance = (amount, ccy) => {
    if (!format) return formatCurrency(amount, ccy);
    const native = format(amount, ccy);
    if (!convertOn || !primary || primary === ccy) return native;
    const c = convert(amount, ccy, primary);
    if (c.currency === ccy) return native; // conversion not yet available
    return (
      <>
        {native}
        <span className="block text-[10px] text-[var(--muted-text)] font-normal mt-0.5">
          ≈ {format(c.amount, c.currency)}
        </span>
      </>
    );
  };

  return (
    <div className="space-y-6" data-testid="banks-overview">
      {/* Multi-currency rails — USD (Plaid + USD manual) and NGN (Mono + NGN manual) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="banks-currency-rails">
        {isActive("USD") && (
        <div className="card-flat p-5 border-l-4 border-l-[#1E5A96]" data-testid="bank-rail-usd">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">USD Rail · Plaid</div>
              <div className="font-display text-2xl mt-1" data-testid="bank-rail-usd-balance">
                {renderBalance(usdManual, "USD")}
              </div>
              <div className="text-xs text-[var(--muted-text)] mt-1">
                {usdConnected} connected · {manualByCcy.USD ? "incl. manual USD" : "manual USD $0.00"}
              </div>
            </div>
            <div className="w-9 h-9 rounded-md bg-[#1E5A96] text-white flex items-center justify-center shrink-0">
              <Banknote className="w-4 h-4" />
            </div>
          </div>
        </div>
        )}
        {isActive("NGN") && (
        <div className="card-flat p-5 border-l-4 border-l-[#4C956C]" data-testid="bank-rail-ngn">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">NGN Rail · Mono</div>
              <div className="font-display text-2xl mt-1" data-testid="bank-rail-ngn-balance">
                {renderBalance(ngnManual, "NGN")}
              </div>
              <div className="text-xs text-[var(--muted-text)] mt-1">
                {ngnConnected} connected · {manualByCcy.NGN ? "incl. manual NGN" : "manual NGN ₦0.00"}
              </div>
            </div>
            <div className="w-9 h-9 rounded-md bg-[#4C956C] text-white flex items-center justify-center shrink-0">
              <Banknote className="w-4 h-4" />
            </div>
          </div>
        </div>
        )}
      </div>

      {/* FX-conversion helper text — nudges the user toward the sidebar toggle */}
      <div
        data-testid="banks-fx-hint"
        className={`card-flat p-3 flex items-center gap-2 text-xs ${
          convertOn ? "border-l-4 border-l-[var(--brand)]" : "border-l-4 border-l-[var(--border)]"
        }`}
      >
        <span className="text-[var(--muted-text)]">
          {convertOn
            ? <>FX conversion <b className="text-[var(--brand)]">ON</b> — non-{primary} rails also show an equivalent in {primary}. Flip the toggle in the sidebar to see native-only values.</>
            : <>FX conversion <b>OFF</b> — showing native currency only. Flip the FX toggle in the sidebar to convert between Dollars ⇄ Naira.</>
          }
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Connected banks" value={totalConnected}
                  sublabel={`${data.counts.plaid} Plaid · ${data.counts.mono} Mono`}
                  testid="bank-stat-connected" />
        <StatCard label="Total institutions" value={data.counts.total}
                  sublabel="Plaid + Mono + Manual"
                  testid="bank-stat-total" />
        <StatCard label="Manual accounts" value={data.counts.manual}
                  sublabel="Track any bank yourself"
                  testid="bank-stat-manual-count" />
        {(() => {
          const byCcy = data.totals.manual_by_currency || {};
          const rails = Object.keys(byCcy).sort((a, b) => (a === primary ? -1 : b === primary ? 1 : a.localeCompare(b)));
          if (rails.length === 0) {
            return (
              <StatCard label="Manual balance" value={0} currency={primary}
                        sublabel="No manual accounts yet"
                        tone="brand" testid="bank-stat-manual-balance" />
            );
          }
          const [first, ...rest] = rails;
          return (
            <div data-testid="bank-stat-manual-balance" className="card-flat p-4 border-l-4 border-l-[var(--brand)]">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted-text)]">Manual balance</div>
              <div className="mt-1 space-y-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-lg font-semibold tabular-nums" data-testid={`bank-stat-manual-${first}`}>
                    {formatCurrency(byCcy[first], first)}
                  </span>
                  <span className="text-[10px] font-mono text-[var(--muted-text)]">{first}</span>
                </div>
                {rest.map((c) => (
                  <div key={c} className="flex items-baseline gap-2">
                    <span className="text-sm font-medium tabular-nums text-[var(--muted-text)]" data-testid={`bank-stat-manual-${c}`}>
                      {formatCurrency(byCcy[c], c)}
                    </span>
                    <span className="text-[10px] font-mono text-[var(--muted-text)]">{c}</span>
                  </div>
                ))}
              </div>
              <div className="mt-1 text-[10px] text-[var(--muted-text)]">
                {rails.length > 1 ? `Native totals per rail (${rails.length} currencies)` : "Native total"}
              </div>
            </div>
          );
        })()}
      </div>

      <div className="card-flat overflow-hidden">
        <div className="px-4 py-3 border-b border-[var(--border)] text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">
          All banks
        </div>
        {data.counts.total === 0 ? (
          <div className="p-10 text-center text-sm text-[var(--muted-text)]">
            No banks yet. Connect via Plaid/Mono, or add one manually in the Manual tab.
          </div>
        ) : (
          <table className="qb-table w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--muted-text)] border-b border-[var(--border)]">
                <th className="px-4 py-2 font-medium">Bank</th>
                <th className="px-4 py-2 font-medium">Account</th>
                <th className="px-4 py-2 font-medium">Provider</th>
                <th className="px-4 py-2 font-medium">Currency</th>
                <th className="px-4 py-2 font-medium text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {[...data.manual_banks, ...data.plaid_banks, ...data.mono_banks]
                .filter((b) => isActive(b.currency || "USD"))
                .map((b, i) => {
                const ccy = b.currency || "USD";
                return (
                  <tr key={`${b.provider}-${b.id}-${i}`} data-testid={`bank-row-${b.id}`}>
                    <td className="px-4 py-2.5 font-medium">{b.institution_name || "—"}</td>
                    <td className="px-4 py-2.5 text-[var(--muted-text)]">
                      <div className="flex flex-col gap-1">
                        <span>{b.account_name || "—"}</span>
                        {b.provider === "manual" && reconStatus[b.id] && <ReconBadge status={reconStatus[b.id]} />}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                        b.provider === "plaid" ? "bg-blue-100 text-blue-800" :
                        b.provider === "mono" ? "bg-purple-100 text-purple-800" :
                        "bg-[var(--brand-light)] text-[var(--brand-text)]"
                      }`}>{b.provider}</span>
                    </td>
                    <td className="px-4 py-2.5 font-mono-data text-xs">{ccy}</td>
                    <td className="px-4 py-2.5 text-right font-mono-data font-medium">
                      {b.balance == null ? (
                        <span className="text-[var(--muted-text)] text-xs">— sync to fetch</span>
                      ) : renderBalance(b.balance, ccy)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Manual Banks — CRUD on Accounts (type=bank / with institution_name)
// ============================================================
function ManualBanks({ onChange }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const reconStatus = useReconStatus();

  function blankForm() {
    return { name: "", type: "bank", institution_name: "", starting_balance: 0, currency: "USD" };
  }

  const load = async () => {
    try {
      const { data } = await api.get("/accounts");
      // manual "banks" = any account with institution_name set OR type=bank
      const banks = data.filter((a) => a.type === "bank" || (a.institution_name && a.institution_name.trim()));
      setRows(banks);
    } catch (err) {
      console.warn("[ManualBanks] load failed:", err?.message);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Bank name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type,
        institution_name: form.institution_name.trim() || null,
        starting_balance: parseFloat(form.starting_balance) || 0,
        currency: form.currency,
      };
      if (editing) {
        await api.patch(`/accounts/${editing.id}`, payload);
        toast.success("Bank updated");
      } else {
        await api.post("/accounts", payload);
        toast.success("Manual bank added");
      }
      setOpen(false);
      setEditing(null);
      setForm(blankForm());
      load();
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!removeTarget) return;
    try {
      await api.delete(`/accounts/${removeTarget.id}`);
      toast.success(`${removeTarget.institution_name || removeTarget.name} removed`);
      setRemoveTarget(null);
      load();
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="manual-banks">
      <div className="card-flat p-4 flex items-start gap-3 border-l-4 border-l-[var(--brand)]">
        <Wallet className="w-5 h-5 text-[var(--brand)] mt-0.5 shrink-0" />
        <div className="text-sm text-[var(--muted-text)]">
          Add banks manually when Plaid/Mono don't cover your region or you just want to track balances by hand. Manual balances contribute to the Balance Sheet and Banks Overview.
        </div>
      </div>

      <div className="flex justify-between items-center">
        <div className="text-sm text-[var(--muted-text)]">
          {rows.length} manual bank{rows.length === 1 ? "" : "s"}
        </div>
        <Button
          data-testid="add-manual-bank-btn"
          className="rounded-md btn-brand h-9"
          onClick={() => { setEditing(null); setForm(blankForm()); setOpen(true); }}
        >
          <Plus className="w-4 h-4 mr-1" /> Add manual bank
        </Button>
      </div>

      {rows.length === 0 ? (
        <div className="card-flat p-10 text-center text-sm text-[var(--muted-text)]">
          No manual banks yet. Click "Add manual bank" to record a starting balance and track transactions.
        </div>
      ) : (
        <div className="card-flat overflow-hidden">
          <table className="qb-table w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--muted-text)] border-b border-[var(--border)]">
                <th className="px-4 py-2 font-medium">Bank</th>
                <th className="px-4 py-2 font-medium">Account</th>
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium text-right">Starting balance</th>
                <th className="px-4 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`manual-bank-row-${r.id}`}>
                  <td className="px-4 py-2.5 font-medium">{r.institution_name || "—"}</td>
                  <td className="px-4 py-2.5 text-[var(--muted-text)]">
                    <div className="flex flex-col gap-1">
                      <span>{r.name}</span>
                      {reconStatus[r.id] && <ReconBadge status={reconStatus[r.id]} />}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-xs">{r.type}</td>
                  <td className="px-4 py-2.5 text-right font-mono-data">{formatCurrency(r.starting_balance, r.currency)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" onClick={() => {
                        setEditing(r);
                        setForm({
                          name: r.name, type: r.type || "bank",
                          institution_name: r.institution_name || "",
                          starting_balance: r.starting_balance, currency: r.currency,
                        });
                        setOpen(true);
                      }} data-testid={`edit-manual-bank-${r.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => setRemoveTarget(r)}
                              data-testid={`delete-manual-bank-${r.id}`}>
                        <Trash2 className="w-4 h-4 text-[var(--expense)]" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-w-md" data-testid="manual-bank-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit manual bank" : "Add manual bank"}</DialogTitle>
            <DialogDescription>Track balances yourself. Transactions can be added later on the Transactions page.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wide text-[var(--muted-text)]">Bank / Institution name</Label>
              <Input data-testid="mb-institution" value={form.institution_name}
                     onChange={(e) => setForm({ ...form, institution_name: e.target.value })}
                     placeholder="e.g. Chase, HSBC, Kuda" className="mt-1 h-10 rounded-md" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-[var(--muted-text)]">Account nickname</Label>
              <Input data-testid="mb-name" value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder="e.g. Business checking" className="mt-1 h-10 rounded-md" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-[var(--muted-text)]">Type</Label>
                <select data-testid="mb-type"
                        value={form.type}
                        onChange={(e) => setForm({ ...form, type: e.target.value })}
                        className="mt-1 w-full h-10 rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 text-sm">
                  <option value="bank">Bank</option>
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="wallet">Wallet</option>
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-[var(--muted-text)]">Currency</Label>
                <select data-testid="mb-currency"
                        value={form.currency}
                        onChange={(e) => setForm({ ...form, currency: e.target.value })}
                        className="mt-1 w-full h-10 rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 text-sm">
                  <option value="USD">USD</option>
                  <option value="NGN">NGN</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-[var(--muted-text)]">Starting balance</Label>
              <Input data-testid="mb-starting-balance" type="number" step="0.01"
                     value={form.starting_balance}
                     onChange={(e) => setForm({ ...form, starting_balance: e.target.value })}
                     className="mt-1 h-10 rounded-md font-mono-data" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="mb-save" onClick={submit} disabled={saving} className="btn-brand">
              {saving ? "Saving…" : editing ? "Save changes" : "Add bank"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removeTarget?.institution_name || removeTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Existing transactions on this account will be detached, but not deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="mb-delete-confirm" className="bg-[var(--expense)] hover:opacity-90 text-white">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
